import path from 'path';
import fs from 'fs-extra';
import { WorkflowConfig, WorkflowState, Task, AnalysisResult, TaskState, TaskHistoryEntry, TaskActionResult, NextTaskRecommendation, TaskPriority } from '../types';
import { WorkflowRunner } from './workflow-runner';
import { TaskAnalyzer } from './task-analyzer';
import { TaskWatcher } from './task-watcher';
import { logger } from '../utils/logger';
import { loadConfig, saveConfig } from '../utils/config';
import { WorkflowTemplates } from '../templates';
import { WORKFLOW_DIR, TODO_FILE_PATHS } from '../constants';
import { ClaudeIntegration } from './claude-integration';
import { SimpleCache } from '../utils/cache';
import { EnhancedTaskAnalyzer, EnhancedAnalysisResult, TaskType } from './enhanced-task-analyzer';
import { ContextBuilder } from './context/context-builder';
import { KnowledgeBase } from './knowledge-base';
import { TaskSplitter, SplitOptions, SplitResult, SubTask } from './task-splitter';
import { TaskTemplateManager } from './task-template';

export interface WorkflowManagerOptions {
  projectRoot?: string;
  configPath?: string;
  enableAI?: boolean;
}

export class WorkflowManager {
  private projectRoot: string;
  private workflowDir: string;
  private config: WorkflowConfig | null = null;
  private runner: WorkflowRunner;
  private analyzer: TaskAnalyzer;
  private watcher: TaskWatcher | null = null;
  private cache: SimpleCache;

  constructor(options: WorkflowManagerOptions = {}) {
    this.projectRoot = options.projectRoot || process.cwd();
    this.workflowDir = path.join(this.projectRoot, WORKFLOW_DIR);
    this.taskStateFile = path.join(this.workflowDir, 'task-state.json');
    
    this.runner = new WorkflowRunner(this.projectRoot);
    this.analyzer = new TaskAnalyzer({
      enableAI: options.enableAI || false
    });
    this.cache = new SimpleCache({
      cacheDir: path.join(this.workflowDir, '.cache'),
      ttl: 600000 // 10 minutes
    });
  }

  async initialize(force = false): Promise<void> {
    if (await this.isInitialized() && !force) {
      throw new Error('Workflow system already initialized. Use --force to reinitialize.');
    }

    logger.info('Initializing workflow system...');

    // Create directory structure
    const dirs = [
      'commands',
      'workflows',
      'knowledge/patterns',
      'knowledge/decisions',
      'knowledge/troubleshooting',
      'logs'
    ];

    for (const dir of dirs) {
      await fs.ensureDir(path.join(this.workflowDir, dir));
    }

    // Create default configuration
    const config: WorkflowConfig = {
      version: '1.0.0',
      project: {
        name: path.basename(this.projectRoot),
        type: await this.detectProjectType(),
        framework: await this.detectFrameworks(),
        language: await this.detectLanguage()
      },
      settings: {
        autoSync: true,
        syncInterval: 300000, // 5 minutes
        enableAI: false,
        checkMode: 'quick'
      }
    };

    await saveConfig(this.workflowDir, config);
    await this.createDefaultTemplates();

    // Initialize Claude Code integration if available
    const claudeIntegration = new ClaudeIntegration({
      projectRoot: this.projectRoot,
      force: force,
      enableHooks: true,
      safeMode: true
    });

    if (await claudeIntegration.isClaudeProject() || process.env.CLAUDE_CODE) {
      logger.info('Detected Claude Code environment, setting up integration...');
      await claudeIntegration.initialize();
      await claudeIntegration.createWorkflowSymlink();
      logger.success('Claude Code integration configured successfully!');
    }

    logger.success('Workflow system initialized successfully!');
  }

  async isInitialized(): Promise<boolean> {
    return fs.pathExists(this.workflowDir);
  }

  async isClaudeIntegrated(): Promise<boolean> {
    const claudeDir = path.join(this.projectRoot, '.claude');
    const hasClaudeDir = await fs.pathExists(claudeDir);
    const hasWorkflowCommands = await fs.pathExists(path.join(claudeDir, 'commands', 'task.md'));
    return hasClaudeDir && hasWorkflowCommands;
  }

  async loadConfig(): Promise<WorkflowConfig> {
    if (!this.config) {
      // Try cache first
      const cached = await this.cache.get<WorkflowConfig>('config');
      if (cached) {
        this.config = cached;
        return cached;
      }

      // Load from disk
      this.config = await loadConfig(this.workflowDir);
      
      // Cache it
      await this.cache.set('config', this.config);
    }
    return this.config;
  }

  async runCommand(commandName: string): Promise<void> {
    await this.ensureInitialized();
    return this.runner.runCommand(commandName);
  }

  async startWorkflow(type: string, options: { name?: string; description?: string } = {}): Promise<void> {
    await this.ensureInitialized();
    return this.runner.startWorkflow(type, options);
  }

  async getWorkflowStatus(): Promise<WorkflowState | null> {
    await this.ensureInitialized();
    return this.runner.getStatus();
  }

  async analyzeTasks(): Promise<AnalysisResult> {
    await this.ensureInitialized();
    const todoPath = await this.findTodoFile();
    
    if (!todoPath) {
      throw new Error('No TODO file found in project');
    }

    const content = await fs.readFile(todoPath, 'utf-8');
    return this.analyzer.analyze(content);
  }

  async startWatching(): Promise<void> {
    await this.ensureInitialized();
    
    if (this.watcher) {
      logger.warn('Task watcher is already running');
      return;
    }

    const todoPath = await this.findTodoFile();
    if (!todoPath) {
      throw new Error('No TODO file found to watch');
    }

    this.watcher = new TaskWatcher({
      projectRoot: this.projectRoot,
      todoPath,
      workflowDir: this.workflowDir
    });

    await this.watcher.start();
  }

  async stopWatching(): Promise<void> {
    if (this.watcher) {
      await this.watcher.stop();
      this.watcher = null;
    }
  }

  async syncTasks(): Promise<void> {
    await this.ensureInitialized();
    const todoPath = await this.findTodoFile();
    
    if (!todoPath) {
      logger.warn('No TODO file found to sync');
      return;
    }

    const watcher = new TaskWatcher({
      projectRoot: this.projectRoot,
      todoPath,
      workflowDir: this.workflowDir
    });

    await watcher.syncOnce();
  }

  private async ensureInitialized(): Promise<void> {
    if (!await this.isInitialized()) {
      throw new Error('Workflow system not initialized. Run "workflow init" first.');
    }
  }

  private async findTodoFile(): Promise<string | null> {
    for (const todoPath of TODO_FILE_PATHS) {
      const fullPath = path.join(this.projectRoot, todoPath);
      if (await fs.pathExists(fullPath)) {
        return fullPath;
      }
    }

    return null;
  }

  private async detectProjectType(): Promise<string> {
    const packageJsonPath = path.join(this.projectRoot, 'package.json');
    
    if (await fs.pathExists(packageJsonPath)) {
      const pkg = await fs.readJson(packageJsonPath);
      
      if (pkg.workspaces) return 'monorepo';
      if (pkg.dependencies?.next) return 'nextjs-app';
      if (pkg.dependencies?.react) return 'react-app';
      if (pkg.dependencies?.vue) return 'vue-app';
      if (pkg.dependencies?.express) return 'node-app';
      
      return 'node-package';
    }
    
    if (await fs.pathExists(path.join(this.projectRoot, 'Cargo.toml'))) {
      return 'rust-project';
    }
    
    if (await fs.pathExists(path.join(this.projectRoot, 'go.mod'))) {
      return 'go-project';
    }
    
    return 'unknown';
  }

  private async detectFrameworks(): Promise<string[]> {
    const frameworks: string[] = [];
    const packageJsonPath = path.join(this.projectRoot, 'package.json');
    
    if (await fs.pathExists(packageJsonPath)) {
      const pkg = await fs.readJson(packageJsonPath);
      const deps = { ...pkg.dependencies, ...pkg.devDependencies };
      
      if (deps.react) frameworks.push('react');
      if (deps.vue) frameworks.push('vue');
      if (deps.angular) frameworks.push('angular');
      if (deps.next) frameworks.push('nextjs');
      if (deps.express) frameworks.push('express');
      if (deps.fastify) frameworks.push('fastify');
      if (deps.typescript) frameworks.push('typescript');
    }
    
    return frameworks;
  }

  private async detectLanguage(): Promise<string> {
    if (await fs.pathExists(path.join(this.projectRoot, 'tsconfig.json'))) {
      return 'typescript';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'package.json'))) {
      return 'javascript';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'Cargo.toml'))) {
      return 'rust';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'go.mod'))) {
      return 'go';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'requirements.txt'))) {
      return 'python';
    }
    
    return 'unknown';
  }

  private async createDefaultTemplates(): Promise<void> {
    const projectName = path.basename(this.projectRoot);
    
    // Create default command templates
    const commands = {
      'init.md': WorkflowTemplates.getInitCommand({ projectName, projectRoot: this.projectRoot }),
      'todo.md': WorkflowTemplates.getTodoCommand(),
      'context.md': WorkflowTemplates.getContextCommand(projectName),
      'review.md': WorkflowTemplates.getReviewCommand()
    };

    for (const [filename, content] of Object.entries(commands)) {
      await fs.writeFile(
        path.join(this.workflowDir, 'commands', filename),
        content
      );
    }

    // Create default workflow templates
    const workflows = {
      'feature.yaml': WorkflowTemplates.getFeatureWorkflow(),
      'bugfix.yaml': WorkflowTemplates.getBugfixWorkflow(),
      'refactor.yaml': WorkflowTemplates.getRefactorWorkflow()
    };

    for (const [filename, content] of Object.entries(workflows)) {
      await fs.writeFile(
        path.join(this.workflowDir, 'workflows', filename),
        content
      );
    }
  }

  // Task state management for hooks integration
  private taskStateFile: string;

  async getTaskState(): Promise<TaskState | null> {
    try {
      if (await fs.pathExists(this.taskStateFile)) {
        return await fs.readJson(this.taskStateFile);
      }
      return null;
    } catch (error) {
      logger.error('Failed to read task state:', error);
      return null;
    }
  }

  async saveTaskState(state: TaskState): Promise<void> {
    try {
      await fs.writeJson(this.taskStateFile, state, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save task state:', error);
      throw error;
    }
  }

  async updateTaskState(state: TaskState): Promise<void> {
    await this.saveTaskState(state);
  }

  async startTask(taskInfo: {
    task: string;
    priority: 'high' | 'medium' | 'low';
    phase?: TaskState['current']['phase'];
    context?: Record<string, any>;
  }): Promise<TaskActionResult> {
    try {
      const state = await this.getTaskState() || {
        history: [],
        lastUpdate: new Date()
      };

      // If there's a current task, save it to history first
      if (state.current) {
        const historyEntry: TaskHistoryEntry = {
          task: state.current.task,
          status: 'cancelled',
          startTime: state.current.startTime,
          endTime: new Date(),
          duration: Date.now() - new Date(state.current.startTime).getTime(),
          notes: 'Task interrupted by starting new task',
          phase: state.current.phase
        };
        state.history.push(historyEntry);
      }

      // Set the new current task
      state.current = {
        task: taskInfo.task,
        phase: taskInfo.phase || 'understanding',
        startTime: new Date(),
        priority: taskInfo.priority,
        context: taskInfo.context
      };
      state.lastUpdate = new Date();

      await this.saveTaskState(state);
      
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async completeCurrentTask(notes?: string): Promise<TaskActionResult> {
    try {
      const state = await this.getTaskState();
      
      if (!state || !state.current) {
        return { success: false, error: 'No active task to complete' };
      }

      // Move current task to history
      const historyEntry: TaskHistoryEntry = {
        task: state.current.task,
        status: 'completed',
        startTime: state.current.startTime,
        endTime: new Date(),
        duration: Date.now() - new Date(state.current.startTime).getTime(),
        notes,
        phase: state.current.phase
      };
      
      state.history.push(historyEntry);
      state.current = undefined;
      state.lastUpdate = new Date();

      await this.saveTaskState(state);

      // Sync with TODO.md if needed
      await this.syncTaskCompletion(historyEntry.task);
      
      return { success: true, data: historyEntry };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async recommendNextTask(): Promise<NextTaskRecommendation[] | null> {
    try {
      // Read TODO.md file to get current tasks
      const todoPath = path.join(this.projectRoot, 'project-docs/TODO.md');
      if (!await fs.pathExists(todoPath)) {
        return null;
      }
      
      const todoContent = await fs.readFile(todoPath, 'utf-8');
      const analysis = await this.analyzer.analyze(todoContent);
      
      if (!analysis.recommendations || analysis.recommendations.length === 0) {
        return null;
      }

      // Convert recommendations to NextTaskRecommendation format
      const nextTasks: NextTaskRecommendation[] = analysis.recommendations
        .filter(rec => rec.type === 'task')
        .map(rec => ({
          task: rec.title,
          priority: rec.priority,
          reason: rec.description,
          estimatedDuration: this.estimateTaskDuration(rec.title)
        }));

      return nextTasks;
    } catch (error) {
      logger.error('Failed to get task recommendations:', error);
      return null;
    }
  }

  private estimateTaskDuration(task: string): number {
    // Simple heuristic for task duration estimation (in minutes)
    const keywords = {
      'simple': 30,
      'fix': 60,
      'implement': 120,
      'refactor': 180,
      'design': 240,
      'research': 120,
      'test': 90,
      'document': 60
    };

    const lowerTask = task.toLowerCase();
    for (const [keyword, duration] of Object.entries(keywords)) {
      if (lowerTask.includes(keyword)) {
        return duration;
      }
    }
    
    return 60; // Default 1 hour
  }

  private async syncTaskCompletion(taskDescription: string): Promise<void> {
    try {
      // Find TODO.md file
      const todoPath = await this.findTodoFile();
      if (!todoPath) return;

      // Read and update TODO content
      const content = await fs.readFile(todoPath, 'utf-8');
      const lines = content.split('\n');
      
      // Simple pattern matching to find and mark task as completed
      const updatedLines = lines.map(line => {
        if (line.includes(taskDescription) && line.includes('[ ]')) {
          return line.replace('[ ]', '[x]');
        }
        return line;
      });

      await fs.writeFile(todoPath, updatedLines.join('\n'));
    } catch (error) {
      logger.warn('Failed to sync task completion:', error);
    }
  }

  async estimateTaskTime(taskDescription: string, priority: string = 'medium'): Promise<EnhancedAnalysisResult | null> {
    try {
      // Initialize analyzers if not already done
      if (!this.enhancedAnalyzer) {
        // Create simple mock implementations to avoid circular dependencies
        const mockContextEngine = {
          build: async () => ({
            metadata: {},
            task: null,
            history: [],
            knowledge: [],
            tools: [],
            workflow: null,
            totalSize: 0,
            timestamp: new Date(),
            items: [] // Add items array for generatePrompt
          })
        };
        
        const knowledgeBase = new KnowledgeBase(path.join(this.workflowDir, 'knowledge'));
        
        this.enhancedAnalyzer = new EnhancedTaskAnalyzer(
          mockContextEngine as any,
          knowledgeBase,
          { enableAI: false }
        );
      }

      // Create a task object for analysis
      const task: Task = {
        id: Date.now().toString(),
        content: taskDescription,
        priority: priority as TaskPriority,
        status: 'pending',
        position: 0
      };

      // Analyze the task
      const result = await this.enhancedAnalyzer.analyze(task);
      return result;
    } catch (error) {
      logger.error('Failed to estimate task time:', error);
      return null;
    }
  }

  private enhancedAnalyzer?: EnhancedTaskAnalyzer;
  private taskSplitter?: TaskSplitter;
  private templateManager?: TaskTemplateManager;

  async splitTask(taskDescription: string, options: SplitOptions = {}): Promise<SplitResult | null> {
    try {
      // Initialize splitter if not already done
      if (!this.taskSplitter) {
        // Initialize enhanced analyzer first
        if (!this.enhancedAnalyzer) {
          const mockContextEngine = {
            build: async () => ({
              metadata: {},
              task: null,
              history: [],
              knowledge: [],
              tools: [],
              workflow: null,
              totalSize: 0,
              timestamp: new Date(),
              items: []
            })
          };
          
          const knowledgeBase = new KnowledgeBase(path.join(this.workflowDir, 'knowledge'));
          
          this.enhancedAnalyzer = new EnhancedTaskAnalyzer(
            mockContextEngine as any,
            knowledgeBase,
            { enableAI: false }
          );
        }
        
        this.taskSplitter = new TaskSplitter(this.enhancedAnalyzer);
      }

      // Split the task
      const result = await this.taskSplitter.split(taskDescription, options);
      return result;
    } catch (error) {
      logger.error('Failed to split task:', error);
      return null;
    }
  }

  calculateStartTime(taskId: string, subtasks: SubTask[]): number {
    // Build dependency graph and calculate earliest start time
    const earliestStart = new Map<string, number>();
    const times = new Map<string, number>();
    const deps = new Map<string, string[]>();
    
    // Initialize maps
    for (const task of subtasks) {
      times.set(task.id, task.estimatedTime);
      deps.set(task.id, task.dependencies || []);
    }
    
    // Recursive function to calculate earliest start
    const calculate = (id: string): number => {
      if (earliestStart.has(id)) {
        return earliestStart.get(id)!;
      }
      
      const taskDeps = deps.get(id) || [];
      if (taskDeps.length === 0) {
        earliestStart.set(id, 0);
        return 0;
      }
      
      let maxEnd = 0;
      for (const dep of taskDeps) {
        const depStart = calculate(dep);
        const depDuration = times.get(dep) || 0;
        maxEnd = Math.max(maxEnd, depStart + depDuration);
      }
      
      earliestStart.set(id, maxEnd);
      return maxEnd;
    };
    
    return calculate(taskId);
  }

  async saveSubtasks(splitResult: SplitResult): Promise<boolean> {
    try {
      // Create subtasks directory if not exists
      const subtasksDir = path.join(this.workflowDir, 'subtasks');
      await fs.ensureDir(subtasksDir);
      
      // Generate unique ID for this split
      const splitId = `split-${Date.now()}`;
      const filename = `${splitId}.json`;
      
      // Save the split result
      await fs.writeJson(path.join(subtasksDir, filename), {
        ...splitResult,
        createdAt: new Date(),
        splitId
      }, { spaces: 2 });
      
      // Also update the current task state if active
      const state = await this.getTaskState();
      if (state?.current) {
        state.current.splitId = splitId;
        state.current.subtaskCount = splitResult.taskCount;
        await this.saveTaskState(state);
      }
      
      logger.info(`Saved ${splitResult.taskCount} subtasks to ${filename}`);
      return true;
    } catch (error) {
      logger.error('Failed to save subtasks:', error);
      return false;
    }
  }
}